
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { GobiColor } from '@/helpers/color';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import { addComma } from '@/util/number';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import moment from 'moment';
import settingx from '@/store/modules/settingx';
import HighlightAccountsToggle from '@/components/HighlightAccountsToggle.vue';
import Checkbox from 'primevue/checkbox';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
		TableCollapseToggle,
		HighlightAccountsToggle,
		Checkbox,
	},
})
export default class CustomerOutstandingSummaryBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public agingResult: number[] = [];
	public pdcResult: number[] = [];
	public agingAfterPdcResult: number[] = [];

	public series: echarts.ECharts[] | any = [];
	public detailFields: any[] = [
		{
			key: 'total_aging',
			label: 'Total Outstanding',
			textAlign: 'center',
		},
		{
			key: 'total_pdc',
			label: 'Total PDC',
			textAlign: 'center',
		},
		{
			key: 'total_aging_after_pdc',
			label: 'Total Outstanding after PDC',
			textAlign: 'center',
		},
	];

	public isExcludeCurrentAmount: boolean = false;

	public get xAxisData() {
		return !this.isExcludeCurrentAmount
			? [
					'Current Amount',
					'Overdue 0-30',
					'Overdue 31-60',
					'Overdue 61-90',
					'Overdue 91-120',
					'Overdue > 120',
			  ]
			: [
					'Overdue 0-30',
					'Overdue 31-60',
					'Overdue 61-90',
					'Overdue 91-120',
					'Overdue > 120',
			  ];
	}

	public get newSeriesData() {
		const seriesClone = JSON.parse(JSON.stringify(this.series)) as any[];

		if (this.isExcludeCurrentAmount) {
			seriesClone.forEach((item) => {
				item.data!.shift();
			});
		}

		return seriesClone;
	}

	public get periodTotalData() {
		const seriesClone = this.newSeriesData;
		let totalOustanding = 0;
		let totalOutstandingAfterPDC = 0;
		let totalPDC = 0;
		let data: number[] = [];

		seriesClone.forEach((item) => {
			if (item.name === 'Outstanding') {
				data = item.data as number[];
				totalOustanding = data.reduce((a, b) => a + b, 0);
			}

			if (item.name === 'Outstanding after PDC') {
				data = item.data as number[];
				totalOutstandingAfterPDC = data.reduce((a, b) => a + b, 0);
			}

			if (item.name === 'PDC') {
				data = item.data as number[];
				totalPDC = data.reduce((a, b) => a + b, 0);
			}
		});

		return [
			{
				total_aging:
					this.currencySymbol +
					' ' +
					addComma(totalOustanding, gdbx.numDecimal, true),
				total_pdc:
					this.currencySymbol + ' ' + addComma(totalPDC, gdbx.numDecimal, true),
				total_aging_after_pdc:
					this.currencySymbol +
					' ' +
					addComma(totalOutstandingAfterPDC, gdbx.numDecimal, true),
			},
		];
	}

	public get isHighlightAccount() {
		return settingx.now.highlightAccount;
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.basic;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get expensiveHook() {
		const {
			selectedAsOfDate,
			selectedCustomers,
			selectedAgents,
			isHighlightAccount,
		} = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedCustomers,
			selectedAgents,
			isHighlightAccount,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const asOfDate = this.selectedAsOfDate;
		const nowMs = moment().valueOf();
		const msPerDay = 24 * 60 * 60 * 1000;

		// Build manual filter clauses
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const ivdnTable = getTableName('customerIvDns');
		const koTable = getTableName('customerKos');

		const queryHead = await this._ensureTablesLoaded([
			'customerIvDns', 'customerKos',
		] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		// Fetch raw data in parallel
		const [ivdnData, koData] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					TRY_CAST("DUEDATE" AS BIGINT) as dueDate,
					TRY_CAST("LOCALDOCAMT" AS DOUBLE) as amount
				FROM ${ivdnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${customerFilter}${agentFilter}
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					TRY_CAST("TODOCDUEDATE" AS BIGINT) as dueDate,
					TRY_CAST("KOLOCALAMT" AS DOUBLE) as amount,
					TRY_CAST("KOGAINLOSS" AS DOUBLE) as gainLoss,
					TRY_CAST("FROMDOCDATE" AS BIGINT) as koDate
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${asOfDate}
					${customerFilter}${agentFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		// Helper to get aging bucket using Math.floor for consistency
		const getBucket = (dueDate: number): number => {
			const days = Math.floor((asOfDate - dueDate) / msPerDay);
			if (days <= 0) { return 0; } // Current
			if (days <= 30) { return 1; } // 0-30
			if (days <= 60) { return 2; } // 31-60
			if (days <= 90) { return 3; } // 61-90
			if (days <= 120) { return 4; } // 91-120
			return 5; // > 120
		};

		// Process data based on highlightAccount setting
		let filteredIvdnData = ivdnData;
		let filteredKoData = koData;

		if (this.isHighlightAccount) {
			// Build map of customer due amounts (uses ALL KOs for filtering)
			const customerDue: Map<string, number> = new Map();

			for (const ivdn of ivdnData) {
				const days = Math.floor((asOfDate - Number(ivdn.dueDate)) / msPerDay);
				if (days > 0) {
					const code = ivdn.code as string;
					customerDue.set(code, (customerDue.get(code) || 0) + Number(ivdn.amount || 0));
				}
			}

			for (const ko of koData) {
				const days = Math.floor((asOfDate - Number(ko.dueDate)) / msPerDay);
				if (days > 0) {
					const code = ko.code as string;
					const koAmount = Number(ko.amount || 0) + Number(ko.gainLoss || 0);
					customerDue.set(code, (customerDue.get(code) || 0) - koAmount);
				}
			}

			// Filter to customers with non-zero due
			const validCustomers = new Set(
				Array.from(customerDue.entries())
					.filter(([, due]) => Math.round(due) !== 0)
					.map(([code]) => code),
			);

			if (validCustomers.size === 0) {
				this.agingResult = [0, 0, 0, 0, 0, 0];
				this.pdcResult = [0, 0, 0, 0, 0, 0];
				this.agingAfterPdcResult = [0, 0, 0, 0, 0, 0];
				this._buildSeries();
				return;
			}

			// Filter data to valid customers
			filteredIvdnData = ivdnData.filter((row: any) => validCustomers.has(row.code));
			filteredKoData = koData.filter((row: any) => validCustomers.has(row.code));
		}

		this.generateLoadingText(0.95);

		// Initialize results
		this.agingResult = [0, 0, 0, 0, 0, 0];
		this.pdcResult = [0, 0, 0, 0, 0, 0];
		this.agingAfterPdcResult = [0, 0, 0, 0, 0, 0];

		// Process IVDN amounts (add to aging)
		for (const ivdn of filteredIvdnData) {
			const bucket = getBucket(Number(ivdn.dueDate));
			this.agingResult[bucket] += Number(ivdn.amount) || 0;
		}

		// Process KO amounts
		for (const ko of filteredKoData) {
			const bucket = getBucket(Number(ko.dueDate));
			const koAmount = Number(ko.amount || 0) + Number(ko.gainLoss || 0);
			const koDate = Number(ko.koDate);

			if (koDate <= nowMs) {
				// Non-PDC: subtract from aging
				this.agingResult[bucket] -= koAmount;
			} else {
				// PDC: add to PDC result
				this.pdcResult[bucket] += koAmount;
			}
		}

		// Calculate aging after PDC
		for (let i = 0; i < 6; i++) {
			this.agingAfterPdcResult[i] = this.agingResult[i] - this.pdcResult[i];
		}

		this._buildSeries();
		this.generateLoadingText(1);
		this.saveHistory('series');
	}

	private _buildSeries() {
		this.series = [
			{
				name: 'Outstanding',
				data: this.agingResult,
				type: 'bar',
				barWidth: '40%',
			},
			{
				name: 'Outstanding after PDC',
				data: this.agingAfterPdcResult,
				type: 'bar',
				barWidth: '15%',
				color: GobiColor.DEEP_TEAL,
				stack: 'a',
			},
			{
				name: 'PDC',
				data: this.pdcResult,
				type: 'bar',
				barWidth: '15%',
				stack: 'a',
			},
		];
	}
}
